import React, { useState } from 'react';
import { BookOpen, CheckCircle, ChevronLeft, ClipboardList, SkipForward } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { ElementsRenderer } from '../../shared';
import { QUESTION_RESPONSE_STATUS } from '../../../constants';
import { getAssignmentQuestionURL } from '../../../utils/urlHelpers';
import { useParams } from '../../../hooks';
import { buildDrawerQuestionNumbers } from '../../../utils/drawerRows';

const SubQuestionPills = ({
  subQuestions,
  currentSubIndex,
  onJump,
  partLabels = {},
  compact = false
}) => (
  <div className="flex flex-wrap items-center gap-2">
    {subQuestions.map(({ questionId, status }, idx) => {
      const isActive = idx === currentSubIndex;
      const isAnswered = status === QUESTION_RESPONSE_STATUS.ANSWERED;
      const isSkipped = status === QUESTION_RESPONSE_STATUS.SKIPPED;
      const partLabel = partLabels[questionId] || idx + 1;

      return (
        <button
          key={questionId}
          type="button"
          onClick={() => onJump(questionId)}
          title={`Part ${partLabel}${isAnswered ? ' — Answered' : isSkipped ? ' — Skipped' : ''}`}
          className={`flex items-center justify-center rounded-full border-2 text-xs font-semibold transition-all ${
            compact ? 'h-7 w-7' : 'h-8 w-8'
          } ${
            isActive
              ? 'scale-105 border-primary bg-primary text-white shadow-md'
              : isAnswered
                ? 'border-green-500 bg-green-50 text-green-700 hover:bg-green-100'
                : isSkipped
                  ? 'border-orange-400 bg-orange-50 text-orange-700 hover:bg-orange-100'
                  : 'border-gray-300 bg-white text-gray-600 hover:border-primary/50 hover:text-primary'
          }`}
        >
          {isAnswered && !isActive ? (
            <CheckCircle className="h-3.5 w-3.5 text-green-600" />
          ) : isSkipped && !isActive ? (
            <SkipForward className="h-3 w-3 text-orange-500" />
          ) : (
            partLabel
          )}
        </button>
      );
    })}
  </div>
);

const ReadingComprehensionLayout = ({
  readingComprehensionQuestionRef,
  rcContext,
  assignmentData,
  onJump: onJumpProp,
  children
}) => {
  const [activeTab, setActiveTab] = useState('questions');
  const { studentAssignmentId, questionId } = useParams();
  const navigate = useNavigate();

  const subQuestions = React.useMemo(() => {
    if (!rcContext?.subQuestionIds || !assignmentData?.studentResponse) return [];

    return rcContext.subQuestionIds.map((subId) => {
      const response = assignmentData.studentResponse.find((r) => r.questionId === subId);
      return {
        questionId: subId,
        status: response?.status || QUESTION_RESPONSE_STATUS.NOT_VISITED
      };
    });
  }, [rcContext, assignmentData]);

  // Each part's number, less the passage's own - "1", "2a", "2b" under passage 3 - so the
  // pills name a part the way the tray does.
  const partLabels = React.useMemo(() => {
    const numbers = buildDrawerQuestionNumbers({
      sections: assignmentData?.assignment?.sections || [],
      assignmentQuestions: assignmentData?.assignment?.questions || [],
      responses: assignmentData?.studentResponse
    });
    const passagePrefix = numbers[String(rcContext?.rcId)]
      ? `${numbers[String(rcContext.rcId)]}.`
      : '';

    return Object.fromEntries(
      (rcContext?.subQuestionIds || []).map((subId) => {
        const label = numbers[String(subId)] || '';
        return [
          subId,
          passagePrefix && label.startsWith(passagePrefix)
            ? label.slice(passagePrefix.length)
            : label
        ];
      })
    );
  }, [assignmentData, rcContext]);

  const handleJump = (subQuestionId) => {
    if (subQuestionId === questionId) return;
    if (typeof onJumpProp === 'function') {
      onJumpProp(subQuestionId);
      return;
    }
    navigate(getAssignmentQuestionURL({ studentAssignmentId, questionId: subQuestionId }));
  };

  const handlePreviousPart = () => {
    const prevId = rcContext.subQuestionIds[rcContext.subIndex - 1];
    if (prevId) handleJump(prevId);
  };

  const passageContent = (
    <ElementsRenderer elements={readingComprehensionQuestionRef?.elements} />
  );

  return (
    <div className="p-3 md:p-4">
      {/* Desktop split view */}
      <div className="hidden items-start gap-5 md:flex">
        <div className="w-[44%] flex-shrink-0">
          <Card
            className="sticky top-[140px] flex flex-col overflow-hidden border border-border/50 shadow-sm"
            style={{ maxHeight: 'calc(100vh - 180px)' }}
          >
            <div className="flex-shrink-0 border-b border-border/50 px-5 pb-3 pt-4">
              <div className="mb-2 flex items-center gap-2">
                <BookOpen className="h-4 w-4 flex-shrink-0 text-primary" />
                <Badge className="bg-indigo-100 px-2.5 py-0.5 text-xs text-indigo-800">
                  Reading Comprehension
                </Badge>
              </div>
              <h3 className="text-sm font-semibold leading-snug text-foreground">Passage</h3>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 text-sm leading-relaxed text-foreground">
              {passageContent}
            </div>

            <div className="flex-shrink-0 border-t border-border/50 bg-gray-50/60 px-5 pb-4 pt-3">
              <p className="mb-2 text-xs text-muted-foreground">Jump to part:</p>
              <SubQuestionPills
                subQuestions={subQuestions}
                currentSubIndex={rcContext.subIndex}
                onJump={handleJump}
                partLabels={partLabels}
              />
            </div>
          </Card>
        </div>

        <div className="min-w-0 flex-1">
          <div className="mb-3 flex items-center justify-between">
            <Badge variant="outline" className="text-xs font-medium">
              Part {rcContext.subIndex + 1} of {rcContext.totalSub}
            </Badge>
            {rcContext.subIndex > 0 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handlePreviousPart}
                className="h-7 gap-1 px-2 text-xs text-muted-foreground hover:text-foreground"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
                Previous Part
              </Button>
            )}
          </div>
          {children}
        </div>
      </div>

      {/* Mobile tabs */}
      <div className="md:hidden">
        <div className="mb-4 flex overflow-hidden rounded-lg border border-border">
          <button
            type="button"
            onClick={() => setActiveTab('passage')}
            className={`flex flex-1 items-center justify-center gap-1.5 py-2.5 text-sm font-medium transition-colors ${
              activeTab === 'passage'
                ? 'bg-primary text-white'
                : 'bg-white text-muted-foreground hover:bg-muted/50'
            }`}
          >
            <BookOpen className="h-3.5 w-3.5" />
            Passage
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('questions')}
            className={`flex flex-1 items-center justify-center gap-1.5 border-l border-border py-2.5 text-sm font-medium transition-colors ${
              activeTab === 'questions'
                ? 'bg-primary text-white'
                : 'bg-white text-muted-foreground hover:bg-muted/50'
            }`}
          >
            <ClipboardList className="h-3.5 w-3.5" />
            Questions ({rcContext.subIndex + 1}/{rcContext.totalSub})
          </button>
        </div>

        {activeTab === 'passage' ? (
          <div>
            <Card className="overflow-hidden border border-border/50 shadow-sm">
              <div className="border-b border-border/50 bg-gray-50/50 px-4 pb-3 pt-4">
                <div className="mb-1.5 flex items-center gap-2">
                  <BookOpen className="h-4 w-4 text-primary" />
                  <Badge className="bg-indigo-100 text-xs text-indigo-800">Reading Comprehension</Badge>
                </div>
                <p className="text-sm font-semibold leading-snug text-foreground">Passage</p>
              </div>
              <div className="max-h-[62vh] overflow-y-auto px-4 py-4 text-sm leading-relaxed">
                {passageContent}
              </div>
              <div className="border-t border-border/50 bg-gray-50/50 px-4 pb-4 pt-3">
                <p className="mb-2 text-xs text-muted-foreground">Jump to part:</p>
                <SubQuestionPills
                  subQuestions={subQuestions}
                  currentSubIndex={rcContext.subIndex}
                  onJump={handleJump}
                  compact
                />
              </div>
            </Card>
            <Button
              type="button"
              onClick={() => setActiveTab('questions')}
              className="mb-6 mt-4 w-full"
            >
              Go to Questions →
            </Button>
          </div>
        ) : (
          <div>
            <div className="mb-4 flex items-center gap-3 px-1">
              <span className="whitespace-nowrap text-xs text-muted-foreground">Jump to:</span>
              <SubQuestionPills
                subQuestions={subQuestions}
                currentSubIndex={rcContext.subIndex}
                onJump={handleJump}
                partLabels={partLabels}
                compact
              />
            </div>
            {children}
            {rcContext.subIndex > 0 && (
              <div className="mt-3 px-1">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handlePreviousPart}
                  className="w-full gap-1 text-muted-foreground"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                  Previous Part
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReadingComprehensionLayout;
